import React, { useEffect } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useParams, Link } from 'react-router-dom';
import blogPosts from '../data/blogPosts';
import './Blog.css';
import { appStoreUrl } from '../lib/appStore';

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="page-container blog-container">
        <Helmet>
          <title>Article Not Found — Better Parent Everyday</title>
          <meta name="description" content="The article you're looking for doesn't exist." />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="blog-not-found">
          <h1>Article Not Found</h1>
          <p>The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="blog-back-link">Back to all articles</Link>
        </div>
      </div>
    );
  }

  const articleUrl = `https://www.betterparenteveryday.com/blog/${post.slug}`;
  const parsedDate = new Date(post.date);
  const publishDate = isNaN(parsedDate.getTime()) ? new Date().toISOString() : parsedDate.toISOString();
  const modifiedDate = post.lastModified ? new Date(post.lastModified).toISOString() : publishDate;

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.betterparenteveryday.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://www.betterparenteveryday.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": articleUrl
      }
    ]
  };

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.description,
    "datePublished": publishDate,
    "dateModified": modifiedDate,
    "author": {
      "@type": "Organization",
      "name": "Better Parent Everyday",
      "url": "https://www.betterparenteveryday.com",
      "sameAs": [
        "https://instagram.com/betterparenteveryday",
        "https://apps.apple.com/us/app/better-parent-everyday/id6742127631"
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "Better Parent Everyday",
      "url": "https://www.betterparenteveryday.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.betterparenteveryday.com/apple-touch-icon.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "url": articleUrl
  };

  const faqStructuredData = post.faqs && post.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  } : null;

  const howToStructuredData = post.howToSteps && post.howToSteps.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": post.title,
    "description": post.description,
    "step": post.howToSteps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text
    }))
  } : null;

  // Simple markdown-to-HTML renderer for our blog content
  const renderContent = (markdown) => {
    const lines = markdown.trim().split('\n');
    const elements = [];
    let currentList = [];
    let currentParagraph = [];
    let listType = null;
    let key = 0;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        elements.push(
          <p key={key++} dangerouslySetInnerHTML={{ __html: currentParagraph.map(formatInline).join(' ') }} />
        );
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        if (listType === 'ul') {
          elements.push(
            <ul key={key++}>
              {currentList.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
              ))}
            </ul>
          );
        } else {
          elements.push(
            <ol key={key++}>
              {currentList.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
              ))}
            </ol>
          );
        }
        currentList = [];
        listType = null;
      }
    };

    const formatInline = (text) => {
      return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\[(.+?)\]\(((?:[^()]*|\([^()]*\))*)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.trim() === '') {
        flushParagraph();
        flushList();
        continue;
      }

      // Headers
      if (line.startsWith('### ')) {
        flushParagraph();
        flushList();
        elements.push(
          <h3 key={key++} dangerouslySetInnerHTML={{ __html: formatInline(line.slice(4)) }} />
        );
        continue;
      }
      if (line.startsWith('## ')) {
        flushParagraph();
        flushList();
        elements.push(
          <h2 key={key++} dangerouslySetInnerHTML={{ __html: formatInline(line.slice(3)) }} />
        );
        continue;
      }

      // Unordered list
      if (line.match(/^- /)) {
        flushParagraph();
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        currentList.push(line.slice(2));
        continue;
      }

      // Ordered list
      if (line.match(/^\d+\. /)) {
        flushParagraph();
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        currentList.push(line.replace(/^\d+\. /, ''));
        continue;
      }

      // Paragraph — accumulate consecutive lines
      flushList();
      currentParagraph.push(line);
    }

    flushParagraph();
    flushList();
    return elements;
  };

  // Closing call to action: a post can bring its own (campaign + custom
  // product page, e.g. the "What to say" scripts); the rest share the default.
  const cta = post.cta
    ? {
        heading: post.cta.heading,
        text: post.cta.text,
        button: post.cta.button || 'Download free on the App Store',
        url: appStoreUrl(post.cta.campaign, post.cta.ppid),
      }
    : {
        heading: 'Want daily tips like these?',
        text: "Better Parent Everyday delivers one science-backed parenting tip daily, personalized to your child's age. It takes just 5 minutes.",
        button: 'Download free on the App Store',
        url: appStoreUrl('website_blog_post'),
      };
  const ogImage = `https://www.betterparenteveryday.com${post.image || '/og-image.jpg'}`;

  // Find related posts — use curated relatedPosts if available, otherwise fall back to category match
  let relatedPosts;
  if (post.relatedPosts && post.relatedPosts.length > 0) {
    relatedPosts = post.relatedPosts
      .map((slug) => blogPosts.find((p) => p.slug === slug))
      .filter(Boolean);
  } else {
    const sameCategoryPosts = blogPosts.filter(
      (p) => p.slug !== post.slug && p.category === post.category
    );
    const otherPosts = blogPosts.filter(
      (p) => p.slug !== post.slug && p.category !== post.category
    );
    relatedPosts = [...sameCategoryPosts, ...otherPosts].slice(0, 3);
  }

  return (
    <div className="page-container blog-container">
      <Helmet>
        <title>{post.title} — Better Parent Everyday</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={articleUrl} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:site_name" content="Better Parent Everyday" />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:image" content={ogImage} />
        <meta property="article:published_time" content={publishDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="article:section" content={post.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData).replace(/</g, '\\u003c')}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData).replace(/</g, '\\u003c')}
        </script>
        {faqStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(faqStructuredData).replace(/</g, '\\u003c')}
          </script>
        )}
        {howToStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(howToStructuredData).replace(/</g, '\\u003c')}
          </script>
        )}
      </Helmet>

      <article className="blog-post" itemScope itemType="https://schema.org/Article">
        <header className="blog-post-header">
          <nav className="blog-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/blog">Blog</Link>
            <span>/</span>
            <span>{post.category}</span>
          </nav>

          <div className="blog-post-meta">
            <span className="blog-card-category">{post.category}</span>
            <time dateTime={post.date} itemProp="datePublished">
              {new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
            <span>{post.readTime}</span>
          </div>

          <h1 itemProp="headline">{post.title}</h1>
          <p className="blog-post-description" itemProp="description">{post.description}</p>
        </header>

        <div className="blog-post-body" itemProp="articleBody">
          {post.content ? renderContent(post.content) : null}
        </div>

        <footer className="blog-post-footer">
          <div className="blog-post-cta">
            <h3>{cta.heading}</h3>
            <p>{cta.text}</p>
            <a
              href={cta.url}
              className="blog-cta-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              {cta.button}
            </a>
          </div>

          {relatedPosts.length > 0 && (
            <div className="blog-related">
              <h3>More articles you might like</h3>
              <div className="blog-related-grid">
                {relatedPosts.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="blog-related-card"
                  >
                    <span className="blog-card-category">{related.category}</span>
                    <h4>{related.title}</h4>
                    <span className="blog-card-read-time">{related.readTime}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </footer>
      </article>
    </div>
  );
}

export default BlogPost;
